import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Session cache for frequent clients
let clientCache = null;
let clientCacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const clientService = {
  async getCompanyId() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return null;
      const { data } = await supabase
        ?.from('user_profiles')
        ?.select('company_id')
        ?.eq('id', user?.id)
        ?.maybeSingle();
      return data?.company_id || null;
    } catch {
      return null;
    }
  },

  async create(clientData) {
    try {
      console.log('[clientService.create] START', clientData);

      // getUser with timeout — prevents hanging if auth session is stale
      let user = null;
      try {
        const getUserPromise = supabase?.auth?.getUser();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('getUser timeout')), 5000)
        );
        const result = await Promise.race([getUserPromise, timeoutPromise]);
        user = result?.data?.user;
        console.log('[clientService.create] getUser OK, uid:', user?.id);
      } catch (authErr) {
        console.warn('[clientService.create] getUser failed/timeout:', authErr?.message);
        // Proceed without user — created_by will be null
      }

      // getCompanyId with timeout to avoid hanging
      let companyId = null;
      try {
        const companyIdPromise = this.getCompanyId();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('getCompanyId timeout')), 5000)
        );
        companyId = await Promise.race([companyIdPromise, timeoutPromise]);
        console.log('[clientService.create] companyId:', companyId);
      } catch {
        companyId = null;
        console.warn('[clientService.create] getCompanyId failed/timeout, proceeding with null');
      }

      const snakeData = toSnakeCase({
        ...clientData,
        companyId,
        createdBy: user?.id || null
      });

      console.log('[clientService.create] inserting snakeData:', snakeData);

      const { data, error } = await supabase
        ?.from('clients')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      console.log('[clientService.create] insert result — data:', data, 'error:', error);

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      // Invalidate cache
      clientCache = null;

      // Fire system event (non-blocking, best-effort)
      supabase?.from('system_events')?.insert({
        company_id: companyId,
        event_type: 'cliente_creado',
        client_id: data?.id,
        payload_json: { client_name: data?.nombre, phone: data?.telefono }
      })?.then(() => {})?.catch(() => {});

      console.log('[clientService.create] SUCCESS, returning:', data?.id);
      return toCamelCase(data);
    } catch (error) {
      console.error('[clientService.create] FAILED:', error);
      throw error;
    }
  },

  async update(clientId, clientData) {
    try {
      const snakeData = toSnakeCase(clientData);
      const { data, error } = await supabase
        ?.from('clients')
        ?.update({ ...snakeData, updated_at: new Date()?.toISOString() })
        ?.eq('id', clientId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      clientCache = null;
      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update client:', error);
      throw error;
    }
  },

  // Predictive autocomplete search (debounced 300ms on UI side)
  async search(searchTerm, limit = 10) {
    try {
      let companyId = await this.getCompanyId();

      let query = supabase
        ?.from('clients')
        ?.select('id, nombre, empresa, telefono, telefono_alt, email, rfc, type, direccion, ciudad, estado, codigo_postal, company_id, created_at')
        ?.limit(limit);

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      if (searchTerm?.trim()) {
        query = query?.or(
          `nombre.ilike.%${searchTerm}%,telefono.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,rfc.ilike.%${searchTerm}%,empresa.ilike.%${searchTerm}%`
        );
      }

      // Rank by most recent
      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Error searching clients:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to search clients:', error);
      return [];
    }
  },

  // Get frequent clients (for cache/ranking)
  async getFrequent(limit = 20) {
    try {
      const now = Date.now();
      if (clientCache && (now - clientCacheTime) < CACHE_TTL) {
        return clientCache;
      }

      let companyId = await this.getCompanyId();

      let query = supabase
        ?.from('clients')
        ?.select('id, nombre, empresa, telefono, telefono_alt, email, rfc, type, direccion, ciudad, estado, codigo_postal, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data, error } = await query;

      if (error) {
        console.log('Error fetching frequent clients:', error?.message);
        return [];
      }

      const result = toCamelCase(data || []);
      clientCache = result;
      clientCacheTime = now;
      return result;
    } catch (error) {
      console.error('Failed to fetch frequent clients:', error);
      return [];
    }
  },

  async getById(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('clients')
        ?.select('*, vehicles(*)')
        ?.eq('id', clientId)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch client:', error);
      return null;
    }
  },

  async getVehicles(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('vehicles')
        ?.select('*')
        ?.eq('client_id', clientId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch client vehicles:', error);
      return [];
    }
  },

  // Get vehicle history for a client
  async getHistory(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id, folio_ot, estado_ot, fecha_ingreso, fecha_entrega, total, created_at,
          vehicle:vehicles(id, marca, modelo, year, placas, vin, color)
        `)
        ?.eq('client_id', clientId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch client history:', error);
      return [];
    }
  },

  async getAll(limit = 200) {
    try {
      let companyId = await this.getCompanyId();
      let query = supabase?.from('clients')?.select('id, nombre, empresa, telefono, telefono_alt, email, rfc, direccion, ciudad, estado, codigo_postal, created_at, updated_at')?.order('nombre', { ascending: true })?.limit(limit);

      if (companyId) query = query?.eq('company_id', companyId);

      const { data, error } = await query;
      if (error) {
        console.log('Error fetching all clients:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch all clients:', error);
      return [];
    }
  },

  // Upsert client from Reception — avoids duplicates by RFC, email, phone, or name+company
  async upsertFromReception(clientData) {
    try {
      let companyId = await this.getCompanyId();
      const { nombre, empresa, telefono, email, rfc } = clientData;

      // Build OR conditions for deduplication
      const orParts = [];
      if (rfc?.trim()) orParts?.push(`rfc.eq.${rfc?.trim()?.toUpperCase()}`);
      if (email?.trim()) orParts?.push(`email.ilike.${email?.trim()}`);
      if (telefono?.trim()) orParts?.push(`telefono.eq.${telefono?.trim()}`);

      let existingClient = null;

      if (orParts?.length > 0) {
        let query = supabase?.from('clients')?.select('*')?.or(orParts?.join(','))?.limit(1);
        if (companyId) query = query?.eq('company_id', companyId);
        const { data } = await query;
        if (data && data?.length > 0) existingClient = data?.[0];
      }

      // Fallback: match by nombre + empresa
      if (!existingClient && nombre?.trim() && empresa?.trim()) {
        let query = supabase?.from('clients')?.select('*')?.ilike('nombre', nombre?.trim())?.ilike('empresa', empresa?.trim())?.limit(1);
        if (companyId) query = query?.eq('company_id', companyId);
        const { data } = await query;
        if (data && data?.length > 0) existingClient = data?.[0];
      }

      if (existingClient) {
        // Always update all provided fields (not just missing ones)
        const snakeData = toSnakeCase(clientData);
        const updates = {};
        for (const [key, val] of Object.entries(snakeData)) {
          if (val !== undefined && val !== null && val !== '') updates[key] = val;
        }
        if (Object.keys(updates)?.length > 0) {
          const { data, error } = await supabase?.from('clients')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', existingClient?.id)?.select()?.single();
          if (!error) return toCamelCase(data);
        }
        return toCamelCase(existingClient);
      }

      // Create new client
      return await this.create(clientData);
    } catch (error) {
      console.error('Failed to upsert client from reception:', error);
      throw error;
    }
  }
};